import { useMutation, useQuery } from "@tanstack/react-query";
import {
	fetchAuditYears,
	fetchDistrictEmployeeByCode,
	fetchEmployeeCodesByRoleId,
	fetchExpenditureYears,
	fetchHlcRegistrationNumbers,
	fetchIssueCategories,
	fetchIssueSubCategories,
	fetchIssueTypes,
	fetchLatestAuditCalendar,
	fetchLatestAuditCalendarForSchool,
	fetchMGNREGSById,
	fetchMGNREGSSubmissions,
	fetchSchemes,
	fetchSchoolListByUdiseCode,
	fetchSubSchemesByScheme,
	fetchUdiseCodesByDistrictId,
	fetchVillageEmployeeByMobile,
	submitMGNREGSData,
	submitSchemeDataEntry,
	updateMGNREGSData,
	uploadSchemeReport,
} from "../api/SchemeDataEntry.api";

export const useSchemesList = () => {
	return useQuery({
		queryKey: ["schemes-list"],
		queryFn: () => fetchSchemes(),
	});
};

export const useSubSchemesForScheme = (schemeId: number | null) => {
	const query = useQuery({
		queryKey: ["sub-schemes-for-scheme", schemeId],
		queryFn: () => fetchSubSchemesByScheme(schemeId as number),
		enabled: !!schemeId,
	});

	return {
		...query,
		subSchemes: query.data ?? [],
	};
};

export const useSchemeSubmit = () => {
	return useMutation({
		mutationFn: (data: any) => submitSchemeDataEntry(data),
	});
};

export const useSchemeUpdate = () => {
	return useMutation({
		mutationFn: ({ id, data }: { id: number | string; data: any }) =>
			updateMGNREGSData(id, data),
	});
};

export const useMGNREGSSubmissions = (page = 1, limit = 10) => {
	const query = useQuery({
		queryKey: ["mgnregs-submissions", page, limit],
		queryFn: () => fetchMGNREGSSubmissions(page, limit),
	});

	return {
		...query,
		submissions: query.data?.items ?? [],
		totalItems: query.data?.totalItems ?? 0,
		totalPages: query.data?.totalPages ?? 0,
	};
};

export const useMGNREGSById = (id: number | string | undefined) => {
	return useQuery({
		queryKey: ["mgnregs", id],
		queryFn: () => fetchMGNREGSById(id!),
		enabled: !!id,
	});
};

// Latest finalized (NIRNAY Status = Yes) audit calendar entry matching the
// exact scheme + District/Block/Panchayat/Gram Sabha Date the user has
// picked — used to auto-fill the rest of Section B: Basic Details on a new
// entry. Only fires once all five are present.
export const useLatestAuditCalendar = (
	schemeId: number | null,
	districtId: number | null,
	blockId: number | null,
	villageId: number | null,
	sgsDate: string | null,
) => {
	return useQuery({
		queryKey: [
			"latest-audit-calendar",
			schemeId,
			districtId,
			blockId,
			villageId,
			sgsDate,
		],
		queryFn: () =>
			fetchLatestAuditCalendar(
				schemeId as number,
				districtId as number,
				blockId as number,
				villageId as number,
				sgsDate as string,
			),
		enabled: !!schemeId && !!districtId && !!blockId && !!villageId && !!sgsDate,
	});
};

// Same as useLatestAuditCalendar above, but for school-based schemes (NMP,
// CMBFS) — matched by District/UDISE Code/School Name/Gram Sabha Date.
export const useLatestAuditCalendarForSchool = (
	schemeId: number | null,
	districtId: number | null,
	udiseCode: string | null,
	schoolId: number | null,
	sgsDate: string | null,
) => {
	return useQuery({
		queryKey: [
			"latest-audit-calendar-school",
			schemeId,
			districtId,
			udiseCode,
			schoolId,
			sgsDate,
		],
		queryFn: () =>
			fetchLatestAuditCalendarForSchool(
				schemeId as number,
				districtId as number,
				udiseCode as string,
				schoolId as number,
				sgsDate as string,
			),
		enabled:
			!!schemeId && !!districtId && !!udiseCode && !!schoolId && !!sgsDate,
	});
};

export const useExpenditureYears = () => {
	return useQuery({
		queryKey: ["expenditure-years"],
		queryFn: () => fetchExpenditureYears(),
	});
};

export const useAuditYears = () => {
	return useQuery({
		queryKey: ["audit-years"],
		queryFn: () => fetchAuditYears(),
	});
};

export const useEmployeeCodesByRoleId = (roleId: number | null) => {
	return useQuery({
		queryKey: ["employee-codes", roleId],
		queryFn: () => fetchEmployeeCodesByRoleId(roleId!),
		enabled: !!roleId,
	});
};

export const useDistrictEmployeeByCode = (empCode: string | null) => {
	return useQuery({
		queryKey: ["district-employee", empCode],
		queryFn: () => fetchDistrictEmployeeByCode(empCode!),
		enabled: !!empCode,
	});
};

export const useVillageEmployeeByMobile = (mobileNo: string | null) => {
	return useQuery({
		queryKey: ["village-employee", mobileNo],
		queryFn: () => fetchVillageEmployeeByMobile(mobileNo!),
		enabled: !!mobileNo,
	});
};

export const useUdiseCodes = (
	districtId: number | null,
	schemeId?: number | string | null,
) => {
	return useQuery({
		queryKey: ["udise-codes", districtId, schemeId],
		queryFn: () => fetchUdiseCodesByDistrictId(districtId!, schemeId),
		enabled: !!districtId,
	});
};

export const useSchoolsByUdiseCode = (
	udiseCode: string | null,
	districtId?: number | string | null,
	schemeId?: number | string | null,
) => {
	return useQuery({
		queryKey: ["schools-by-udise", udiseCode, districtId, schemeId],
		queryFn: () => fetchSchoolListByUdiseCode(udiseCode!, districtId, schemeId),
		enabled: !!udiseCode,
	});
};

export const useIssueTypes = (schemeId?: number | null) => {
	return useQuery({
		queryKey: ["issue-types", schemeId],
		queryFn: () => fetchIssueTypes(schemeId),
	});
};

export const useIssueCategories = (issueTypeId: number | null) => {
	return useQuery({
		queryKey: ["issue-categories", issueTypeId],
		queryFn: () => fetchIssueCategories(issueTypeId!),
		enabled: !!issueTypeId,
	});
};

export const useIssueSubCategories = (categoryId: number | null) => {
	return useQuery({
		queryKey: ["issue-sub-categories", categoryId],
		queryFn: () => fetchIssueSubCategories(categoryId!),
		enabled: !!categoryId,
	});
};

export const useUploadSchemeReport = () => {
	return useMutation({
		mutationFn: (file: File) => uploadSchemeReport(file),
	});
};

export const useHlcRegistrationNumbers = (
	districtId?: number | string | null,
	schemeId?: number | string | null,
) => {
	return useQuery({
		queryKey: ["hlc-registration-numbers", districtId, schemeId],
		queryFn: () => fetchHlcRegistrationNumbers(districtId, schemeId),
		enabled: Boolean(districtId || schemeId),
	});
};
